import { Component, EventEmitter, Output, signal, computed, Input, OnInit, OnDestroy, SimpleChanges, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { matTooltipSmartImports } from '@app/shared/imports/mat-tooltip-smart';
import { CopyableTruncateTooltipDirective } from '@app/shared/directives/copyable-truncate-tooltip.directive';
import { NgSelectModule } from '@ng-select/ng-select';
import { ApiService } from '@app/core/services/API/api-service';
import { Subject, Subscription, takeUntil, filter, debounceTime, merge, map, finalize } from 'rxjs';
import { Spinner } from '@app/shared/components/spinner/spinner';
import { ConfirmPopup } from '@app/shared/components/confirm-popup/confirm-popup';
import { InvoiceDetailModal } from '@app/pages/clients/invoice-detail-modal/invoice-detail-modal';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { ProjectsService } from '@app/pages/projects/projects.service';
import { CrossTabSyncService, SYNC_CLIENT_SUMMARY_PINNED_UPDATES_CHANGED } from '@app/core/services/cross-tab-sync/cross-tab-sync.service';
import { ClientSummarySyncService } from '@app/core/services/client-summary-sync/client-summary-sync.service';
import type { ClientSummarySliderFilter } from '@app/core/services/client-summary-sync/client-summary-sync.service';
import {
  buildSummarySyncPayload,
  extractClientSummaryItems,
  findClientSummaryEntryBySummaryId,
  isSummaryDocumentItem,
  isSummaryUpdateItem,
} from '@app/core/utils/client-summary-pinned-update.util';

interface Note {
  id: string;
  user: string;
  userImage?: string;
  date: string;
  title: string;
  type: 'links' | 'images' | 'document' | 'table';
  source?: 'projects' | 'updates' | 'tasks' | 'documents' | 'requests' |'issues' |
  'revisions';
  objCode?: string; // Original object code from API
  objID?: string;
  projectID?: string; // Project ID for navigation
  content: {
    greeting?: string;
    url?: string;
    docUrl?: string;
    bulletPoints?: string[];
    images?: string[];
    fileName?: string;
    tableText?: string;
    tableHeaders?: string[];
    tableData?: string[][];
    textHTML?: string;
    docServerURL?: string;
    docName?: string;
    docSize?: string;
    docFormat?: string;
  };
}
interface Portfolio {
  _id: string;
  portfolioManager: {
    _id: string;
    name: string;
    image: string;
  };
  portfolioManagerID: string;
  portfolioName: string;
  portfolioDescription: string;
  isActive: boolean;
  status: string;
  createdBy: {
    _id: string;
    name: string;
    image: string;
  };
  lastUpdatedBy: {
    _id: string;
    name: string;
    image: string;
  };
  companyName: string;
  companyID: number;
  isElite: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  email: string;
  phone: string;
  accountManager?: {
    _id: string;
    email: string;
    name: string;
  };
}
// API Response interface
interface ApiNote {
  _id: string;
  status: string;
  objCode: string;
  objID: string;
  projectID: string;
  portfolioID: string;
  pinnedBy: {
    _id: string;
    userID: string;
    name: string;
    email: string;
    utype: string;
    accesslevelID: string;
    accesslevelType: string;
    accesslevel_name: string;
    homeTeamID: string;
    agencyID: string;
    agencyDbID: string;
    en_id: string;
    jobInfo: string;
    image: string;
    timezone: string;
    managerID: string;
    accessor: string;
  };
  createdAt: string;
  updatedAt: string;
  __v: number;
  objData: {
    _id: string;
    status: string;
    isSystemUpdate?: boolean;
    textHTML?: string;
    notify?: string[];
    objCode: string;
    objID: string;
    projectID: string;
    parentID?: string;
    isReply?: boolean;
    createdBy?: {
      _id: string;
      userID: string;
      name: string;
      email: string;
      utype: string;
      accesslevelID: string;
      accesslevelType: string;
      accesslevel_name: string;
      homeTeamID: string;
      agencyID: string;
      agencyDbID: string;
      en_id: string;
      jobInfo: string;
      image: string;
      timezone: string;
      managerID: string;
      accessor: string;
    };
    // Document specific fields
    docName?: string;
    docSize?: string;
    docFormat?: string;
    docServerURL?: string;
    uploadedBy?: {
      _id: string;
      userID: string;
      name: string;
      email: string;
      utype: string;
      accesslevelID: string;
      accesslevelType: string;
      accesslevel_name: string;
      homeTeamID: string;
      agencyID: string;
      agencyDbID: string;
      en_id: string;
      jobInfo: string;
      image: string;
      timezone: string;
      managerID: string;
      accessor: string;
    };
    createdAt: string;
    updatedAt: string;
    __v: number;
  };
  // Additional fields for project and portfolio data
  projectData?: any[];
  portfolioData?: any[];
}

@Component({
  selector: 'app-client-summary-slider',
  standalone: true,
  imports: [
    CommonModule, 
    MatIconModule, 
    FormsModule,
    MatButtonModule,
    ...matTooltipSmartImports,
    CopyableTruncateTooltipDirective,
    NgSelectModule,
    Spinner,
    ConfirmPopup,
    InvoiceDetailModal
  ],
  templateUrl: './client-summary-slider.html',
  styleUrls: ['./client-summary-slider.scss']
})
export class ClientSummarySlider implements OnInit, OnDestroy,OnChanges {
  @Output() close = new EventEmitter<void>();
  @Input() context: { objCode: string, objID: string } = { objCode: 'CLIENT', objID: '' };
  @Input() contentFilter: ClientSummarySliderFilter = 'all';
  @Input() companyId: string | number | null = null;
  @Input() initialInvoices: any[] | null = null;
  @Input() initialInvoiceFromAddress: any | null = null;

  // Dropdown state
  activeDropdownId = signal<string | null>(null);
  
  // API data
  clientSummaryData = signal<any>(null);
  isLoading = signal<boolean>(false);
  showConfirmPopup = signal<boolean>(false);
  noteToRemove = signal<string | null>(null);
  isRemoving = signal<boolean>(false);
  portfolios = signal<Portfolio[]>([]);
  selectedPortfolio = signal<Portfolio | null>(null);
  isLoadingPortfolios = signal<boolean>(false);
  noDataFound = signal<boolean>(false);
  hasError = signal<boolean>(false);
  errorMessage = signal<string>('');
  departments = signal<any[]>([]);
  selectedDepartment = signal<any>(null);
  isLoadingDepartments = signal<boolean>(false);
  
  // Store current project data for displaying project owner
  currentProjectData = signal<any>(null);
  
  // Computed property for account manager name from selected portfolio
  accountManagerName = computed(() => {
    const portfolio = this.selectedPortfolio();
    return portfolio?.accountManager?.name || '';
  });


  private subscription = new Subscription();
  private hasLoaded = false; // Flag to prevent multiple loads
  private currentContext: { objCode: string, objID: string } | null = null;
  private destroy$ = new Subject<void>();
  private isSettingPortfolioProgrammatically = false; // Flag to prevent onPortfolioChange from triggering API calls
  private lastLoadedPortfolioId: string | null = null; // Track last loaded portfolio to prevent duplicate calls
  private hasUserManuallySelectedPortfolio = false; // Flag to track if user has manually selected a portfolio

 



  // Sort options
  sortOptions = [
    { id: 1, name: 'Date (Newest First)' },
    { id: 2, name: 'Date (Oldest First)' },
    { id: 3, name: 'User Name (A-Z)' },
    { id: 4, name: 'User Name (Z-A)' },
    { id: 5, name: 'Title' }
  ];
  
  selectedSort = this.sortOptions[0]; // Default to Date (Newest First)

  // Search functionality
  searchQuery = signal<string>('');
  filteredNotes = signal<Note[]>([]);
  sliderInvoices = signal<any[]>([]);
  isLoadingInvoices = signal<boolean>(false);
  invoiceFromAddress = signal<any>(null);
  showInvoiceModal = signal<boolean>(false);
  selectedInvoice = signal<any>(null);

  constructor(
    private apiService: ApiService,
    private toastr: ToastrService,
    private router: Router,
    private crossTabSyncService: CrossTabSyncService,
    private clientSummarySync: ClientSummarySyncService,
    private projectsService: ProjectsService,
  ) {}

  ngOnInit(): void {
    // Load portfolios first, then check and load data
    // This ensures portfolios are available when we need to auto-select from project data
    this.getPortfolio();
    this.loadDepartments();
    // Use setTimeout to ensure portfolios start loading before we check data
    setTimeout(() => {
      this.checkAndLoadData();
    }, 100);

    this.setupClientSummaryRefreshListeners();
  }

  private setupClientSummaryRefreshListeners(): void {
    merge(
      this.clientSummarySync.refresh$.pipe(
        map((p) => p.portfolioId),
        filter((id): id is string => !!id)
      ),
      this.crossTabSyncService.getSyncEvents().pipe(
        filter(
          (e): e is NonNullable<typeof e> =>
            !!e && e.type === SYNC_CLIENT_SUMMARY_PINNED_UPDATES_CHANGED && !!e.data?.portfolioId
        ),
        map((e) => e.data.portfolioId as string)
      )
    )
      .pipe(debounceTime(80), takeUntil(this.destroy$))
      .subscribe((portfolioId) => this.refreshClientSummaryAfterPin(portfolioId));
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contentFilter']) {
      if (this.contentFilter === 'invoices') {
        this.loadInvoicesForSlider(true);
      } else if (!changes['contentFilter'].firstChange) {
        this.handleContentFilterChange();
      }
    }

    if (
      (changes['initialInvoices'] || changes['initialInvoiceFromAddress']) &&
      this.contentFilter === 'invoices'
    ) {
      this.loadInvoicesForSlider(true);
    }

    if (changes['companyId'] && this.contentFilter === 'invoices') {
      if (!changes['companyId'].firstChange || !this.applyInitialInvoicesIfAvailable()) {
        this.loadSliderInvoices(true);
      }
    }

    // Check if context has changed and reload if necessary
    if (changes['context'] && !changes['context'].firstChange) {
      this.checkAndLoadData();
    }
  }

  private handleContentFilterChange(): void {
    if (this.contentFilter === 'invoices') {
      this.loadInvoicesForSlider(true);
      return;
    }
    this.applySearchAndSort();
    if (!this.hasLoaded && this.context.objID && this.context.objID !== 'default-client-id') {
      this.checkAndLoadData();
    }
  }

  private loadInvoicesForSlider(force = false): void {
    if (this.applyInitialInvoicesIfAvailable()) {
      return;
    }
    this.loadSliderInvoices(force);
  }

  private applyInitialInvoicesIfAvailable(): boolean {
    if (this.contentFilter !== 'invoices' || !Array.isArray(this.initialInvoices)) {
      return false;
    }

    this.sliderInvoices.set(this.initialInvoices);
    this.invoiceFromAddress.set(this.initialInvoiceFromAddress);
    this.isLoadingInvoices.set(false);
    return true;
  }

  isInvoicesView(): boolean {
    return this.contentFilter === 'invoices';
  }

  /** Expand portfolio select width only when opened from client detail page. */
  isClientDetailPageContext(): boolean {
    const url = this.router.url.split('?')[0];
    return (
      /\/clients\/[^/]+(?:\/|$)/.test(url) ||
      /\/client-details\/[^/]+(?:\/|$)/.test(url)
    );
  }

  /** Portfolio switch is locked when opened from View all invoices. */
  isPortfolioSelectRestricted(): boolean {
    return this.isInvoicesView();
  }

  private checkAndLoadData(): void {
    if (this.context.objID && this.context.objID !== 'default-client-id') {
      if (this.contentFilter === 'invoices') {
        this.currentContext = { ...this.context };
        if (this.context.objCode === 'PORTFOLIO') {
          this.selectPortfolioById(this.context.objID);
        }
        this.loadInvoicesForSlider();
        return;
      }

      // Check if context has changed or if we haven't loaded yet
      const contextChanged = !this.currentContext || 
        this.currentContext.objCode !== this.context.objCode || 
        this.currentContext.objID !== this.context.objID;
      
      if (contextChanged || !this.hasLoaded) {
        this.currentContext = { ...this.context };
        
        // Reset manual selection flag when context changes (new project/page)
        // This allows auto-selection to work for new contexts
        if (contextChanged) {
          this.hasUserManuallySelectedPortfolio = false;
        }
        
        // If context is PROJECT, fetch project details to get portfolio data
        if (this.context.objCode === 'PROJECT') {
          this.loadProjectAndUsePortfolio();
        } else {
          // Bind the opened client/portfolio in the dropdown (documents, comments, all)
          if (this.context.objCode === 'PORTFOLIO') {
            this.selectPortfolioById(this.context.objID);
          }
          this.loadClientSummary();
        }
      }
    } else {
      // Show message that no valid context is provided
      console.warn('No valid context provided for client summary');
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
  getPortfolio(): void {
    this.isLoadingPortfolios.set(true);
    this.apiService.getPortfolios().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (response.success && response.data && Array.isArray(response.data)) {
          this.portfolios.set(response.data);
          this.bindPortfolioFromContext();
        } else {
          console.warn('No portfolios data received or invalid format');
          this.toastr.warning('No portfolios available');
        }
        this.isLoadingPortfolios.set(false);
      },
      error: (error) => {
        console.error('Error loading portfolios:', error);
        this.toastr.error('Failed to load portfolios');
        this.isLoadingPortfolios.set(false);
      }
    });
  }

  /** Keep / restore portfolio ng-select value from the slider open context. */
  private bindPortfolioFromContext(): void {
    if (this.hasUserManuallySelectedPortfolio) {
      return;
    }

    const portfolioId =
      this.context?.objCode === 'PORTFOLIO' && this.context.objID && this.context.objID !== 'default-client-id'
        ? this.context.objID
        : this.selectedPortfolio()?._id;

    if (!portfolioId) {
      return;
    }

    const matching = this.portfolios().find((p) => p._id === portfolioId);
    if (matching) {
      this.isSettingPortfolioProgrammatically = true;
      this.selectedPortfolio.set(matching);
      setTimeout(() => {
        this.isSettingPortfolioProgrammatically = false;
      }, 100);
    }
  }

  /**
   * Load project details to extract portfolio data and use it for client summary
   * First tries to get project data from service (already loaded), otherwise fetches it
   */
  private loadProjectAndUsePortfolio(): void {
    if (!this.context.objID) {
      console.warn('No project ID provided');
      return;
    }

    // First, try to get project data from service (already loaded by project-details component)
    const cachedProjectData = this.projectsService.getCurrentProjectData();
    
    if (cachedProjectData && cachedProjectData._id === this.context.objID) {
      // Use cached project data - no API call needed
      this.useProjectDataForPortfolio(cachedProjectData);
      return;
    }

    // If not in cache, fetch it (fallback scenario)
    this.isLoading.set(true);
    
    this.subscription.add(
      this.projectsService.getProjectById(this.context.objID).subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.useProjectDataForPortfolio(response.data);
          } else {
            console.warn('Invalid project response');
            this.loadClientSummary();
          }
          this.isLoading.set(false);
        },
        error: (error: unknown) => {
          console.error('Error loading project details:', error);
          // Fallback to regular project context on error
          this.loadClientSummary();
          this.isLoading.set(false);
        }
      })
    );
  }
  
  /**
   * Extract portfolio data from project data and use it for client summary
   */
  private useProjectDataForPortfolio(projectData: any): void {
    // Store project data for displaying project owner and other info
    this.currentProjectData.set(projectData);
    
    const portfolioData = projectData.portfolioData;
    
    if (portfolioData && portfolioData._id) {
      // Use portfolio data for client summary
      const portfolioId = portfolioData._id;
      
      // Check if this portfolio is already selected to avoid duplicate API calls
      const currentPortfolio = this.selectedPortfolio();
      const isAlreadySelected = currentPortfolio && currentPortfolio._id === portfolioId;
      
      // Only auto-select the portfolio if user hasn't manually selected one
      // This prevents overriding user's manual selection
      if (!isAlreadySelected && !this.hasUserManuallySelectedPortfolio) {
        this.selectPortfolioById(portfolioId, portfolioData);
        // Load client summary using portfolio (only if not already loading)
        if (!this.isLoading()) {
          this.loadClientSummaryForPortfolio(portfolioId);
        }
      } else if (isAlreadySelected && !this.hasUserManuallySelectedPortfolio) {
        // Portfolio is already selected and user hasn't manually changed it, just load data
        if (!this.isLoading()) {
          this.loadClientSummaryForPortfolio(portfolioId);
        }
      }
      // If user has manually selected a portfolio, don't override it
    } else {
      // No portfolio data found, fallback to regular project context
      console.warn('No portfolio data found in project response');
      this.loadClientSummary();
    }
  }

  /**
   * Select portfolio by ID, creating a temporary portfolio if not found in list
   */
  private selectPortfolioById(portfolioId: string, portfolioData?: any): void {
    // Check if portfolio is already selected to avoid unnecessary updates
    const currentPortfolio = this.selectedPortfolio();
    if (currentPortfolio && currentPortfolio._id === portfolioId) {
      return; // Already selected, no need to update
    }
    
    this.isSettingPortfolioProgrammatically = true;
    const allPortfolios = this.portfolios();
    const matchingPortfolio = allPortfolios.find(p => p._id === portfolioId);
    
    if (matchingPortfolio) {
      this.selectedPortfolio.set(matchingPortfolio);
    } else if (portfolioData) {
      // Create a temporary portfolio object from project data
      const tempPortfolio: Portfolio = {
        _id: portfolioData._id,
        portfolioManagerID: portfolioData.portfolioManagerID || '',
        portfolioName: portfolioData.portfolioName || '',
        portfolioDescription: portfolioData.portfolioDescription || '',
        isActive: portfolioData.status === 'active',
        status: portfolioData.status || 'active',
        createdBy: portfolioData.createdBy || {} as any,
        lastUpdatedBy: portfolioData.lastUpdatedBy || {} as any,
        companyName: portfolioData.companyName || '',
        companyID: portfolioData.companyID || 0,
        isElite: portfolioData.isElite || false,
        createdAt: portfolioData.createdAt || '',
        updatedAt: portfolioData.updatedAt || '',
        __v: portfolioData.__v || 0,
        email: portfolioData.email || '',
        phone: portfolioData.phone || '',
        portfolioManager: portfolioData.portfolioManager || {} as any,
        accountManager: portfolioData.accountManager || undefined
      };
      this.selectedPortfolio.set(tempPortfolio);
      
      // Also add it to the portfolios list if not already there
      if (!allPortfolios.find(p => p._id === portfolioId)) {
        this.portfolios.set([...allPortfolios, tempPortfolio]);
      }
    } else {
      // Wait a bit for portfolios to load, then try again once
      setTimeout(() => {
        const updatedPortfolios = this.portfolios();
        const foundPortfolio = updatedPortfolios.find(p => p._id === portfolioId);
        if (foundPortfolio) {
          this.selectedPortfolio.set(foundPortfolio);
        }
        this.isSettingPortfolioProgrammatically = false;
      }, 500);
      return; // Exit early, flag will be reset in setTimeout
    }
    
    // Reset flag after a short delay to allow Angular change detection to complete
    setTimeout(() => {
      this.isSettingPortfolioProgrammatically = false;
    }, 100);
  }
  loadDepartments(): void {
    this.isLoadingDepartments.set(true);
    
    this.projectsService.getProjectPhase().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: { data?: unknown[] } | null) => {
        console.log('Project phases response:', response);
        if (response && response.data && Array.isArray(response.data)) {
          // Map the API response to department format
          const departmentOptions = response.data.map((phase: any) => ({
            id: phase._id,
            name: phase.phase,
            value: phase._id
          }));
          
          // Add "All" option at the beginning
          const allOption = { id: 'all', name: 'All', value: 'all' };
          this.departments.set([allOption, ...departmentOptions]);
          
          // Set "All" as default selection
          this.selectedDepartment.set(allOption);
        } else {
          console.warn('No project phases data received or invalid format');
          // Fallback to "All" option only
          const allOption = { id: 'all', name: 'All', value: 'all' };
          this.departments.set([allOption]);
          this.selectedDepartment.set(allOption);
        }
        this.isLoadingDepartments.set(false);
      },
      error: (error: unknown) => {
        console.error('Error loading project phases:', error);
        this.toastr.error('Failed to load departments');
        // Fallback to "All" option only
        const allOption = { id: 'all', name: 'All', value: 'all' };
        this.departments.set([allOption]);
        this.selectedDepartment.set(allOption);
        this.isLoadingDepartments.set(false);
      }
    });
  }


  onPortfolioChange(portfolio: Portfolio): void {
    // Skip if we're setting portfolio programmatically to avoid duplicate API calls
    if (this.isSettingPortfolioProgrammatically) {
      return;
    }

    // Invoice view is scoped to the opened client — portfolio switch is feature-restricted
    if (this.isPortfolioSelectRestricted()) {
      return;
    }
    
    // Mark that user has manually selected a portfolio
    this.hasUserManuallySelectedPortfolio = true;
    
    this.selectedPortfolio.set(portfolio);
    // Reset last loaded portfolio ID when user manually changes portfolio
    this.lastLoadedPortfolioId = null;
    // Filter notes based on selected portfolio
    if (portfolio) {
      this.filterNotesByPortfolio(portfolio._id);
    } else {
      // If no portfolio selected, show all notes
      
    }
  }

  onDepartmentChange(department: any): void {
    this.selectedDepartment.set(department);
    console.log('Department changed to:', department);
     this.lastLoadedPortfolioId = null;   
    // Reload client summary data with department filter
    if (this.selectedPortfolio()) {
      this.loadClientSummaryForPortfolio(this.selectedPortfolio()!._id);
    } else if (this.context.objID && this.context.objID !== 'default-client-id') {
      this.loadClientSummary();
    }
  }
  onSortChange(sortOption: any): void {
    this.selectedSort = sortOption;
    this.applySorting();
  }

  // Search functionality
  onSearchChange(searchValue: string): void {
    this.searchQuery.set(searchValue);
    this.applySearchAndSort();
  }

  onSearchClear(): void {
    this.searchQuery.set('');
    this.applySearchAndSort();
  }

  private applyContentTypeFilter(notes: Note[]): Note[] {
    if (this.contentFilter === 'invoices') {
      return [];
    }
    if (this.contentFilter === 'documents') {
      return notes.filter((note) => note.type === 'document');
    }
    if (this.contentFilter === 'comments') {
      return notes.filter((note) => note.type !== 'document');
    }
    return notes;
  }

  loadSliderInvoices(force = false): void {
    const companyId = this.companyId ?? this.selectedPortfolio()?.companyID ?? null;
    if (!companyId) {
      this.sliderInvoices.set([]);
      this.isLoadingInvoices.set(false);
      return;
    }

    if (this.isLoadingInvoices() && !force) {
      return;
    }

    this.isLoadingInvoices.set(true);
    this.apiService
      .getInvoiceData(String(companyId))
      .pipe(
        finalize(() => this.isLoadingInvoices.set(false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response: any) => {
          const invoices = Array.isArray(response?.data?.invoice)
            ? response.data.invoice
            : [];
          this.sliderInvoices.set(invoices);
          this.invoiceFromAddress.set(response?.data?.from_address ?? null);
        },
        error: () => {
          this.sliderInvoices.set([]);
          this.toastr.error('Failed to load invoices');
        },
      });
  }

  onSliderInvoiceClick(invoice: any): void {
    this.selectedInvoice.set(invoice);
    this.showInvoiceModal.set(true);
  }

  onSliderInvoiceModalClose(): void {
    this.showInvoiceModal.set(false);
    this.selectedInvoice.set(null);
  }

  isInvoicePaid(invoice: any): boolean {
    return String(invoice?.paymentStatus ?? '').toLowerCase() === 'paid';
  }

  getSliderCompanyIdString(): string {
    return this.companyId != null ? String(this.companyId) : '';
  }

  private applySearchAndSort(): void {
    const currentNotes = this.notes();
    if (!currentNotes || currentNotes.length === 0) {
      this.filteredNotes.set([]);
      return;
    }

    let filteredNotes: Note[] = [...currentNotes];

    filteredNotes = this.applyContentTypeFilter(filteredNotes);

    // Apply search filter
    const searchTerm = this.searchQuery().toLowerCase().trim();
    if (searchTerm) {
      filteredNotes = filteredNotes.filter(note => {
        const titleMatch = note.title.toLowerCase().includes(searchTerm);
        const userMatch = note.user.toLowerCase().includes(searchTerm);
        const contentMatch = note.content.textHTML && note.content.textHTML.toLowerCase().includes(searchTerm);
        const docNameMatch = note.content.docName && note.content.docName.toLowerCase().includes(searchTerm);
        const badgeTextMatch = this.getBadgeText(note).toLowerCase().includes(searchTerm);
        const sourceMatch = note.source && note.source.toLowerCase().includes(searchTerm);
        
        // Also search in other content fields
        const greetingMatch = note.content.greeting && note.content.greeting.toLowerCase().includes(searchTerm);
        const urlMatch = note.content.url && note.content.url.toLowerCase().includes(searchTerm);
        const docUrlMatch = note.content.docUrl && note.content.docUrl.toLowerCase().includes(searchTerm);
        const fileNameMatch = note.content.fileName && note.content.fileName.toLowerCase().includes(searchTerm);
        
        return titleMatch || userMatch || contentMatch || docNameMatch || badgeTextMatch || sourceMatch || 
               greetingMatch || urlMatch || docUrlMatch || fileNameMatch;
      });
    }

    // Apply sorting
    this.applySortingToNotes(filteredNotes);
  }

  private applySortingToNotes(notesToSort: Note[]): void {
    let sortedNotes: Note[] = [...notesToSort];

    switch (this.selectedSort.id) {
      case 1: // Date (Newest First)
        sortedNotes.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
      case 2: // Date (Oldest First)
        sortedNotes.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        break;
      case 3: // User Name (A-Z)
        sortedNotes.sort((a, b) => a.user.localeCompare(b.user));
        break;
      case 4: // User Name (Z-A)
        sortedNotes.sort((a, b) => b.user.localeCompare(a.user));
        break;
      case 5: // Title
        sortedNotes.sort((a, b) => a.title.localeCompare(b.title));
        break;
      default:
        // Default to newest first
        sortedNotes.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
    }

    this.filteredNotes.set(sortedNotes);
  }

  applySorting(): void {
    this.applySearchAndSort();
  }

  filterNotesByPortfolio(portfolioId: string): void {
    if (!portfolioId) {
      console.warn('No portfolio ID provided for filtering');
      return;
    }

    // Load portfolio-specific client summary
    this.loadClientSummaryForPortfolio(portfolioId);
  }

  /** Reload summary when pin/unpin/remove affects this portfolio */
  private refreshClientSummaryAfterPin(portfolioId: string): void {
    if (!portfolioId) {
      return;
    }

    if (this.contentFilter === 'invoices') {
      this.loadInvoicesForSlider(true);
      return;
    }

    if (this.context?.objCode === 'PORTFOLIO' && this.context.objID === portfolioId) {
      this.loadClientSummary(true);
      return;
    }

    this.loadClientSummaryForPortfolio(portfolioId, true);
  }

  loadClientSummaryForPortfolio(portfolioId: string, force = false): void {
    if (!portfolioId) {
      return;
    }

    if (force) {
      this.lastLoadedPortfolioId = null;
      if (this.isLoading()) {
        this.subscription.unsubscribe();
        this.subscription = new Subscription();
        this.isLoading.set(false);
      }
    }

    // Prevent multiple simultaneous calls
    if (this.isLoading()) {
      return;
    }

    // Prevent duplicate calls for the same portfolio
    if (!force && this.lastLoadedPortfolioId === portfolioId && !this.hasError()) {
      return;
    }

    this.lastLoadedPortfolioId = portfolioId;
    this.isLoading.set(true);
    
    // Clean up any existing subscription before making new call
    this.subscription.unsubscribe();
    this.subscription = new Subscription();
    const departmentParam = this.selectedDepartment()?.value || 'all';
    this.subscription.add(
      this.apiService.getClientSummary('PORTFOLIO', portfolioId,departmentParam).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.clientSummaryData.set(response.data);
            this.noDataFound.set(false);
            this.hasError.set(false);
            this.errorMessage.set('');
            // Process API response data
            if (Array.isArray(response.data)) {
              const mappedNotes = this.mapApiResponseToNotes(response.data);
              this.notes.set(mappedNotes);
              this.filteredNotes.set(mappedNotes);
              this.applySearchAndSort();
            } else if (response.data.notes && Array.isArray(response.data.notes)) {
              const mappedNotes = this.mapApiResponseToNotes(response.data.notes);
              this.notes.set(mappedNotes);
              this.filteredNotes.set(mappedNotes);
              this.applySearchAndSort();
            }
          } else if (response.success === false && response.message === "No summary data found") {
            // Handle no data found response
            this.clientSummaryData.set(null);
            this.notes.set([]);
            this.filteredNotes.set([]);
            this.noDataFound.set(true);
            console.log('No summary data found for this portfolio');
          } else {
            // Handle other unsuccessful responses
            this.clientSummaryData.set(null);
            this.notes.set([]);
            this.filteredNotes.set([]);
            this.noDataFound.set(false);
            console.warn('Unexpected response format:', response);
          }
          this.isLoading.set(false);
        },
        error: (error) => {
          this.clientSummaryData.set(null);
          this.notes.set([]);
          this.noDataFound.set(false);
          this.hasError.set(true);
          this.errorMessage.set('Failed to load portfolio summary');
          console.error('Error loading client summary for portfolio:', error);
          this.toastr.error('Failed to load portfolio summary');
          this.isLoading.set(false);
          // Reset last loaded portfolio ID on error so user can retry
          this.lastLoadedPortfolioId = null;
        }
      })
    );
  }
  loadClientSummary(force = false): void {
    if (!this.context.objID || this.context.objID === 'default-client-id') {
      console.warn('No valid context provided for client summary');
      return;
    }

    if (force && this.isLoading()) {
      this.subscription.unsubscribe();
      this.subscription = new Subscription();
      this.isLoading.set(false);
    }

    // Prevent multiple simultaneous calls
    if (this.isLoading()) {
      console.log('Client summary is already loading, skipping duplicate call');
      return;
    }

    this.isLoading.set(true);
    this.hasLoaded = true; // Mark as loaded to prevent duplicate calls
    
    // Clean up any existing subscription before making new call
    this.subscription.unsubscribe();
    this.subscription = new Subscription();
    const departmentParam = this.selectedDepartment()?.value || 'all';
    this.subscription.add(
      this.apiService.getClientSummary(this.context.objCode, this.context.objID, departmentParam).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.clientSummaryData.set(response.data);
            this.noDataFound.set(false);
            this.hasError.set(false);
            this.errorMessage.set('');
            // Process API response data
            if (Array.isArray(response.data)) {
              const mappedNotes = this.mapApiResponseToNotes(response.data);
              this.notes.set(mappedNotes);
              this.filteredNotes.set(mappedNotes);
              this.applySearchAndSort();
            } else if (response.data.notes && Array.isArray(response.data.notes)) {
              const mappedNotes = this.mapApiResponseToNotes(response.data.notes);
              this.notes.set(mappedNotes);
              this.filteredNotes.set(mappedNotes);
              this.applySearchAndSort();
            }
          } else if (response.success === false && response.message === "No summary data found") {
            // Handle no data found response
            this.clientSummaryData.set(null);
            this.notes.set([]);
            this.filteredNotes.set([]);
            this.noDataFound.set(true);
            console.log('No summary data found for this client');
          } else {
            // Handle other unsuccessful responses
            this.clientSummaryData.set(null);
            this.notes.set([]);
            this.filteredNotes.set([]);
            this.noDataFound.set(false);
            console.warn('Unexpected response format:', response);
          }
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error loading client summary:', error);
          this.clientSummaryData.set(null);
          this.notes.set([]);
          this.noDataFound.set(false);
          this.hasError.set(true);
          this.errorMessage.set('Failed to load client summary');
          this.isLoading.set(false);
        }
      })
    );
  }

  // Map API response to display format
  private mapApiResponseToNotes(apiNotes: ApiNote[]): Note[] {
    return apiNotes.map(apiNote => {
      const objCode =  apiNote.objCode;
      const isDocument = objCode === 'DOCUMENT';
      
      return {
        id: apiNote._id,
        user: apiNote.pinnedBy?.name || 'Unknown User',
        userImage:   apiNote.pinnedBy?.image || '',
        date: this.formatDate(apiNote.objData?.createdAt || apiNote.createdAt),
        title: this.getDocumentTitle(apiNote),
        type: isDocument ? 'document' : 'links',
        source: this.mapObjCodeToSource(objCode),
        objCode: apiNote.objCode,
        objID: apiNote.objID,
        projectID: apiNote.projectID || apiNote.objData?.projectID, 
        content: {
          textHTML: isDocument ? '' : (apiNote.objData?.textHTML || ''),
          docServerURL: isDocument ? (apiNote.objData?.docServerURL || '') : '',
          docName: isDocument ? (apiNote.objData?.docName || '') : '',
          docSize: isDocument ? (apiNote.objData?.docSize || '') : '',
          docFormat: isDocument ? (apiNote.objData?.docFormat || '') : ''
        }
      };
    });
  }

  // Get appropriate title for document or other types
  private getDocumentTitle(apiNote: ApiNote): string {
    if (apiNote.projectData && apiNote.projectData.length > 0) {
      return apiNote.projectData[0].projectName || 'Untitled';
    }
    return apiNote.objData?.projectID || apiNote.projectID || 'Untitled';
  }

  // Determine file type based on docFormat
  getFileType(docFormat: string | undefined): 'image' | 'pdf' | 'video' | 'other' {
    if (!docFormat) return 'other';
    
    const format = docFormat.toLowerCase();
    if (['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'].includes(format)) {
      return 'image';
    } else if (format === '.pdf') {
      return 'pdf';
    } else if (['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm'].includes(format)) {
      return 'video';
    }
    return 'other';
  }

  // Check if file is an image
  isImageFile(docFormat: string | undefined): boolean {
    return this.getFileType(docFormat) === 'image';
  }

  // Check if file is a PDF
  isPdfFile(docFormat: string | undefined): boolean {
    return this.getFileType(docFormat) === 'pdf';
  }

  // Check if file is a video
  isVideoFile(docFormat: string | undefined): boolean {
    return this.getFileType(docFormat) === 'video';
  }

  // Open document in new tab
  openDocument(url: string): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

    // Map objCode to source for badge display
    private mapObjCodeToSource(objCode: string): 'projects' | 'updates' | 'tasks' | 'documents' | 'requests' |'issues' |'revisions' {
      switch (objCode) {
        case 'PROJECT':
          return 'projects';
        case 'UPDATE':
          return 'updates';
        case 'TASK':
          return 'tasks';
        case 'DOCUMENT':
          return 'documents';
        case 'REQUEST':
          return 'requests';
        case 'ISSUE':
            return 'issues';
        case 'REVISION':
            return 'revisions';
        default:
          return 'updates';
      }
    }
  
    // Format date for display
    private formatDate(dateString: string): string {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }

  // Method to refresh data when context changes
  refreshClientSummary(): void {
    this.loadClientSummary();
  }

  // Get the context type for display purposes
  getContextType(): string {
    if (this.context.objCode === 'PROJECT') {
      return 'Project';
    } else if (this.context.objCode === 'PORTFOLIO') {
      return 'Client';
    } else {
      return 'Client';
    }
  }

  // Get the context ID for display purposes
  getContextId(): string {
    return this.context.objID;
  }

  // Get the object code for debugging/display purposes
  getObjectCode(): string {
    return this.context.objCode;
  }

  // Sample data matching the image exactly
  notes = signal<Note[]>([ ]);

  // getUserIconClass(note: Note): string {
  //   // Match the exact colors from the image
  //   if (note.user === 'Aziz') return 'purple';
  //   if (note.user === 'McKenna' && note.type === 'images') return 'green';
  //   if (note.user === 'Bonnie') return 'green';
  //   if (note.user === 'McKenna' && note.type === 'table') return 'yellow';
  //   return 'purple'; // fallback
  // }

  // Get user initials for avatar placeholder
  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  // Generate consistent avatar color based on user name
  getAvatarColor(name: string): string {
    if (!name) return '#6b7280';
    
    // Rich vibrant colors
    const colors = [
      '#8b5cf6', // Purple
      '#10b981', // Green
      '#f59e0b', // Yellow/Amber
      '#3b82f6', // Blue
      '#ef4444', // Red
      '#ec4899', // Pink
      '#14b8a6', // Teal
      '#f97316', // Orange
      '#6366f1', // Indigo
      '#a855f7', // Purple
      '#06b6d4', // Cyan
      '#84cc16', // Lime
    ];

    // Generate hash from name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';

    // Find and show the avatar placeholder
    const avatarContainer = img.closest('.user-avatar') as HTMLElement;
    if (avatarContainer) {
      const placeholder = avatarContainer.querySelector('.avatar-placeholder') as HTMLElement;
      if (placeholder) {
        placeholder.style.display = 'flex';
      }
    }
  }

  // Toggle dropdown for note options
  toggleNoteDropdown(event: Event, noteId: string): void {
    event.stopPropagation();
    
    if (this.activeDropdownId() === noteId) {
      this.activeDropdownId.set(null);
    } else {
      this.activeDropdownId.set(noteId);
      
      // Close dropdown when clicking outside
      setTimeout(() => {
        const closeDropdown = () => {
          this.activeDropdownId.set(null);
          document.removeEventListener('click', closeDropdown);
        };
        document.addEventListener('click', closeDropdown);
      }, 0);
    }
  }


  // Check if dropdown is active for a note
  isDropdownActive(noteId: string): boolean {
    return this.activeDropdownId() === noteId;
  }

  /** True when the note card body has visible content (HTML and/or document). */
  hasNoteCardContent(note: Note): boolean {
    if (note.type === 'document') {
      return true;
    }
    return this.hasMeaningfulHtmlContent(note.content?.textHTML);
  }

  private hasMeaningfulHtmlContent(html: string | undefined | null): boolean {
    if (!html?.trim()) {
      return false;
    }
    const text = html
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return text.length > 0;
  }

  // Get badge text based on note source
  getBadgeText(note: Note): string {
    if (!note.source) return 'General';
    
    switch (note.source) {
      case 'projects':
        return 'Projects';
      case 'updates':
        return 'Updates';
      case 'tasks':
        return 'Tasks';
      case 'documents':
        return 'Documents';
      case 'requests':
          return 'Requests';
      case 'issues':
            return 'Issues';
      case 'revisions':
            return 'Revisions';
      default:
        return 'General';
    }
  }

  // Get badge CSS class based on note source
  getBadgeClass(note: Note): string {
    if (!note.source) return 'badge-general';
    
    switch (note.source) {
      case 'projects':
        return 'badge-projects';
      case 'updates':
        return 'badge-updates';
      case 'tasks':
        return 'badge-tasks';
      case 'documents':
        return 'badge-documents';
      default:
        return 'badge-general';
    }
  }

  onClose() {
    // Reset loading state when closing
    this.isLoading.set(false);
    this.hasLoaded = false;
    this.currentContext = null;
    this.noDataFound.set(false);
    this.hasError.set(false);
    this.errorMessage.set('');
    this.lastLoadedPortfolioId = null;
    this.isSettingPortfolioProgrammatically = false;
    this.hasUserManuallySelectedPortfolio = false; // Reset manual selection flag
    this.currentProjectData.set(null); // Clear project data when closing
    this.subscription.unsubscribe();
    this.subscription = new Subscription();
    this.close.emit();
  }

  removeFromClientSummary(noteId: string): void {
    this.noteToRemove.set(noteId);
    this.showConfirmPopup.set(true);
  }

  onConfirmRemove(): void {
    const noteId = this.noteToRemove();
    if (!noteId) return;

    const summaryItems = extractClientSummaryItems(this.clientSummaryData());
    const summaryItem = findClientSummaryEntryBySummaryId(summaryItems, noteId);
    const isUpdateSummary = summaryItem && isSummaryUpdateItem(summaryItem);
    const isDocumentSummary = summaryItem && isSummaryDocumentItem(summaryItem);
    const portfolioId = this.resolvePortfolioIdForPinSync();

    this.isRemoving.set(true);

    this.subscription.add(
      this.apiService.removeFromClientSummary(noteId).subscribe({
        next: (response) => {
          if (!response.success) {
            this.isRemoving.set(false);
            return;
          }

          const finish = (unpinOk: boolean): void => {
            this.toastr.success(response.message || 'Client summary removed successfully');
            if (isUpdateSummary && !unpinOk) {
              this.toastr.warning(
                'Removed from summary, but unpin in Updates may need a refresh'
              );
            } else if (isDocumentSummary && !unpinOk) {
              this.toastr.warning(
                'Removed from summary, but unpin in Documents may need a refresh'
              );
            }
            const currentNotes = this.notes();
            const updatedNotes = currentNotes.filter((note) => note.id !== noteId);
            this.notes.set(updatedNotes);
            this.applySearchAndSort();
            this.showConfirmPopup.set(false);
            this.noteToRemove.set(null);
            this.isRemoving.set(false);
            if (portfolioId) {
              this.clientSummarySync.notifyChanged(
                buildSummarySyncPayload(summaryItem, portfolioId, 'unpinned')
              );
            }
          };

          if (isUpdateSummary && summaryItem) {
            this.clientSummarySync.unpinUpdateFromSummaryItem(summaryItem).subscribe({
              next: (ok) => finish(ok),
              error: () => finish(false),
            });
          } else if (isDocumentSummary && summaryItem) {
            this.clientSummarySync.unpinDocumentFromSummaryItem(summaryItem).subscribe({
              next: (ok) => finish(ok),
              error: () => finish(false),
            });
          } else {
            finish(true);
          }
        },
        error: (error) => {
          this.toastr.error(error.message || 'Failed to remove from client summary');
          console.error('Error removing from client summary:', error);
          this.isRemoving.set(false);
        },
      })
    );
  }

  private resolvePortfolioIdForPinSync(): string {
    return (
      this.selectedPortfolio()?._id ||
      this.currentProjectData()?.portfolioData?._id ||
      (this.context?.objCode === 'PORTFOLIO' ? this.context.objID : '') ||
      ''
    );
  }

  onCancelRemove(): void {
    this.showConfirmPopup.set(false);
    this.noteToRemove.set(null);
  }

  onViewPinClick(note: Note): void {
    if (!note.objCode || !note.objID) {
      this.toastr.warning('Unable to determine the object type or ID for navigation');
      return;
    }

    try {
      let navigationPath: string[] = [];
      let queryParams: any = {};
      let fragment: string | undefined = undefined;
      
      // Use the actual objCode and objID from the note instead of context
      switch (note.objCode) {
        case 'PROJECT':
          // Navigate to project updates
          navigationPath = ['/projects/project-details', note.objID, 'updates'];
          break;
          
        case 'TASK':
          // Navigate to task updates
          navigationPath = ['/task/task-details', note.objID, 'updates'];
          break;
          
        case 'ISSUE':
          // Navigate to issue updates
          navigationPath = ['/issue/issue-details', note.objID, 'updates'];
          break;
          
        case 'REVISION':
          // Navigate to revision updates
          navigationPath = ['/revision/revision-details', note.objID, 'updates'];
          break;
          
        case 'REQUEST':
          // Navigate to request updates
          navigationPath = ['/request/request-details', note.objID, 'updates'];
          break;
          
        case 'DOCUMENT':
          // For documents, navigate to project documents section with document ID for highlighting
          const projectId = note.projectID || this.context.objID;
          if (projectId) {
            navigationPath = ['/projects/project-details', projectId, 'documents'];
            // Add document ID as query parameter to highlight the document
            queryParams = { documentId: note.objID };
          } else {
            this.toastr.warning('Unable to determine project ID for document navigation');
            return;
          }
          break;
          
        case 'UPDATE':
          // For updates, navigate to project updates with update ID fragment for highlighting
          if (note.projectID) {
            navigationPath = ['/projects/project-details', note.projectID, 'updates'];
          } else if (this.context.objID) {
            navigationPath = ['/projects/project-details', this.context.objID, 'updates'];
          } else {
            navigationPath = ['/updates'];
          }
          // Add fragment to highlight the specific update
          fragment = `update-${note.objID}`;
          break;
          
        default:
          // Default to general updates page
          navigationPath = ['/updates'];
          break;
      }

      // Navigate to the determined path with query parameters and fragment if any
      this.router.navigate(navigationPath, { queryParams, fragment });
      
      // Close the client summary slider after navigation
      this.onClose();
      
    } catch (error) {
      console.error('Error navigating to updates page:', error);
      this.toastr.error('Failed to navigate to updates page');
    }
  }
}
